import { useMemo } from "react";
import * as THREE from "three";
import { ThreeEvent } from "@react-three/fiber";

import type { Cabinet } from "@/lib/types";
import { getCabinetClass } from "@/lib/cabinets";
import { evaluateAllDrawers } from "@/lib/cabinets/drawers";
import { evaluateAllLegs } from "@/lib/cabinets/legs";

interface Props {
  cabinet: Cabinet;
  selected: boolean;
  collision?: boolean;
  outOfBounds?: boolean;
  swingBlock?: boolean;
  onSelect: (id: number, shift?: boolean) => void;
}

/**
 * Render one cabinet from its row in `cabinets` collection.
 *
 *   - Resolves the class via `cabinet.classId` and computes mesh chunks
 *     client-side from W/H/D.
 *   - Places the group at `cabinet.position`, rotated around Y.
 *   - For wall-anchored cabinets, lifts off the floor to a typical 54" sill.
 */
export function CabinetMesh({ cabinet, selected, collision = false, outOfBounds = false, swingBlock = false, onSelect }: Props) {
  const cls = getCabinetClass(cabinet.classId);

  const groupY = (() => {
    if (cabinet.anchor === "wall") {
      // Default upper-cabinet underside at 54" off floor unless explicit Y given.
      return cabinet.position[1] || 54;
    }
    return cabinet.position[1] || 0;
  })();

  const chunks = useMemo(() => {
    if (!cls) return [];
    const base = cls.mesh({ w: cabinet.width, h: cabinet.height, d: cabinet.depth });
    // Layer drawer geometry on top so the 3D scene shows the box +
    // face per Cabinet.drawers entry. Drawers are nested objects and
    // not part of the class — see lib/cabinets/drawers.ts.
    const drawerChunks = cabinet.drawers?.length
      ? evaluateAllDrawers(cabinet).meshes
      : [];
    const legChunks = cabinet.legs?.length
      ? evaluateAllLegs(cabinet).meshes
      : [];
    return [...base, ...drawerChunks, ...legChunks];
  }, [cls, cabinet.width, cabinet.height, cabinet.depth, cabinet.drawers, cabinet.legs]);

  if (!cls) {
    // Unknown classId — render a placeholder so the user sees *something*.
    return (
      <mesh
        position={[cabinet.position[0], groupY + 12, cabinet.position[2]]}
        onClick={(e: ThreeEvent<MouseEvent>) => { e.stopPropagation(); onSelect(cabinet._id, e.nativeEvent.shiftKey); }}
      >
        <boxGeometry args={[cabinet.width, cabinet.height, cabinet.depth]} />
        <meshStandardMaterial color="#d96b6b" wireframe />
      </mesh>
    );
  }

  return (
    <group
      position={[cabinet.position[0], groupY, cabinet.position[2]]}
      rotation={[0, cabinet.rotation || 0, 0]}
      onClick={(e: ThreeEvent<MouseEvent>) => { e.stopPropagation(); onSelect(cabinet._id, e.nativeEvent.shiftKey); }}
    >
      {chunks.map((c, i) => (
        <ChunkMesh key={i} chunk={c} selected={selected} />
      ))}
      {(selected || collision || outOfBounds || swingBlock) && (
        <SelectionOutline
          w={cabinet.width}
          h={cabinet.height}
          d={cabinet.depth}
          color={
            collision    ? "#d96b6b" :
            outOfBounds  ? "#ffd633" :
            swingBlock   ? "#6bb6e8" :
                           "#ffaa55"
          }
        />
      )}
    </group>
  );
}

interface ChunkProps {
  chunk: ReturnType<NonNullable<ReturnType<typeof getCabinetClass>>["mesh"]>[number];
  selected: boolean;
}

function ChunkMesh({ chunk, selected }: ChunkProps) {
  const geometry = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(chunk.vertices, 3));
    g.setIndex(chunk.indices);
    g.computeVertexNormals();
    return g;
  }, [chunk.vertices, chunk.indices]);

  return (
    <mesh geometry={geometry} castShadow receiveShadow>
      <meshStandardMaterial
        color={chunk.color ?? "#a47148"}
        roughness={0.7}
        metalness={0}
        emissive={selected ? "#3a2010" : "#000000"}
        emissiveIntensity={selected ? 0.25 : 0}
      />
    </mesh>
  );
}

function SelectionOutline({
  w, h, d, color,
}: { w: number; h: number; d: number; color: string }) {
  // Wireframe box slightly bigger than the cabinet bounds.
  const m = 0.5;
  return (
    <lineSegments position={[w / 2, h / 2, d / 2]}>
      <edgesGeometry args={[new THREE.BoxGeometry(w + m, h + m, d + m)]} />
      <lineBasicMaterial color={color} linewidth={2} />
    </lineSegments>
  );
}
